<template>
  <div
    class="task-details-tabbed"
    :class="{ 'task-details-tabbed--integrated': integrated }"
    data-testid="task-details-tabbed"
  >
    <div class="task-details-tabbed__tabs-wrap" data-testid="task-details-tabbed__tabs-wrap">
      <FcTabSwitcher
        :tabs="tabOptions"
        :value="activeTab"
        variant="underline"
        aria-label="Task detail sections"
        data-testid="task-details-tabbed__tabs"
        @input="onTabChange"
      />
    </div>

    <div
      class="task-details-tabbed__panel task-details-tabbed__scroll-body"
      data-testid="task-details-tabbed__panel"
    >
      <SummaryTab
        v-if="activeTab === 'summary'"
        :task="localTask"
        :conversation-list="conversationList"
        :comments-loading="commentsLoading"
        :dependencies-error="dependenciesError"
        data-testid="task-details-tabbed__panel-summary"
        @getConversation="getConversation"
        @retry-dependencies="$emit('retry-dependencies')"
      />

      <ReconciliationTab
        v-else-if="activeTab === 'reconciliation'"
        :task="task"
        data-testid="task-details-tabbed__panel-reconciliation"
      />

      <FilesRequestsTab
        v-else-if="activeTab === 'files-requests'"
        :task="task"
        :file-names-list="fileNamesList"
        :capabilities="capabilities"
        :viewtaskdata="viewtaskdata"
        :metadata="metadata"
        :inforeqstage="inforeqstage"
        data-testid="task-details-tabbed__panel-files-requests"
        @approve="handleApprove"
        @reject="handleReject"
        @open-upload="$emit('open-upload')"
        @refresh="refreshData"
      />

      <ActivityTab
        v-else-if="activeTab === 'activity'"
        :task="task"
        data-testid="task-details-tabbed__panel-activity"
      />
    </div>
  </div>
</template>

<script>
import { FcTabSwitcher } from '@/modules/financialclose/components';
import SummaryTab from './components/summary/SummaryTab.vue';
import ReconciliationTab from './components/reconciliation/ReconciliationTab.vue';
import FilesRequestsTab from './components/files/FilesRequestsTab.vue';
import ActivityTab from './components/activity/ActivityTab.vue';
import { TASK_DETAIL_TAB } from '@/modules/financialclose/utils/constants';

const TAB_SUMMARY = TASK_DETAIL_TAB.SUMMARY;
const TAB_RECONCILIATION = TASK_DETAIL_TAB.RECONCILIATION;
const TAB_FILES_REQUESTS = TASK_DETAIL_TAB.FILES_REQUESTS;
const TAB_ACTIVITY = TASK_DETAIL_TAB.ACTIVITY;

export default {
  name: 'TaskDetailsTabbed',

  components: {
    FcTabSwitcher,
    SummaryTab,
    ReconciliationTab,
    FilesRequestsTab,
    ActivityTab,
  },

  props: {
    task: {
      type: Object,
      required: true,
    },
    fileNamesList: {
      type: Array,
      default: () => [],
    },
    capabilities: {
      type: Object,
      default: () => ({}),
    },
    initialTab: {
      type: String,
      default: '',
    },
  /** When true, tabs sit inside the task detail header card (no outer card margins). */
    integrated: {
      type: Boolean,
      default: false,
    },
    /** Cached checklist row is showing and getTask did not return dependency lists. */
    dependenciesError: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    const isAccountRecon = this.task?.task_type === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
    const defaultTab = isAccountRecon ? TAB_RECONCILIATION : TAB_SUMMARY;

    return {
      activeTab: this.initialTab || defaultTab,
      viewtaskdata: this.task.viewtaskdata || {},
      conversationList: this.task.viewtaskdata?.conversation || [],
      commentsLoading: false,
      inforeqstage: 0,
      localTask: { ...this.task },
    };
  },

  computed: {
    isAccountRecon() {
      return this.task?.task_type === this.$constants.TASK_LIST_CONSTANTS.TASK_TYPES.ACCOUNT_RECONCILIATION;
    },

    tabOptions() {
      if (this.isAccountRecon) {
        return [
          { id: TAB_RECONCILIATION, label: 'Reconciliation' },
          { id: TAB_SUMMARY, label: 'Summary' },
          { id: TAB_FILES_REQUESTS, label: 'Files & Requests' },
          { id: TAB_ACTIVITY, label: 'Activity' },
        ];
      }
      return [
        { id: TAB_SUMMARY, label: 'Summary' },
        { id: TAB_FILES_REQUESTS, label: 'Files & Requests' },
        { id: TAB_ACTIVITY, label: 'Activity' },
      ];
    },

    taskListData() {
      return this.$store.getters['tasks/getTaskListData'] || {};
    },

    /** Cycle record for comments and files. Store metadata is what a cold load writes. */
    metadata() {
      if (this.task?.metadata?._id) return this.task.metadata;
      if (this.task?.viewtaskdata?.project?._id) return this.task.viewtaskdata.project;
      const stored = this.taskListData?.metadata;
      if (stored?._id) return stored;
      return {};
    },

    /** Empty until both the open task and cycle metadata exist. */
    conversationKey() {
      const metadataId = this.taskListData?.metadata?._id || '';
      const taskId = this.task?._id || '';
      if (!metadataId || !taskId) return '';
      return `${metadataId}:${taskId}`;
    },
  },

  watch: {
    task: {
      handler(newTask) {
        if (newTask?._id) {
          this.localTask = { ...newTask };
        }
      },
      deep: true,
    },
    conversationKey() {
      this.loadConversationIfReady();
    },
  },

  async mounted() {
    this.$emit('tab-change', this.activeTab);
    this.loadConversationIfReady();
  },

  methods: {
    onTabChange(tabId) {
      this.activeTab = tabId;
      this.$emit('tab-change', tabId);
    },

    /** getConversation needs cycle metadata._id and task._id. Skip until both exist. */
    loadConversationIfReady() {
      const metadataId = this.taskListData?.metadata?._id;
      const taskId = this.task?._id;
      if (!metadataId || !taskId) {
        this._loadedConversationKey = '';
        return;
      }
      const key = `${metadataId}:${taskId}`;
      if (this._loadedConversationKey === key) return;
      this._loadedConversationKey = key;
      this.getConversation(this.task);
    },

    async getConversation(task) {
      const metadataId = this.taskListData?.metadata?._id;
      const taskId = task?._id;
      if (!metadataId || !taskId) return;
      this.commentsLoading = true;
      try {
        const result = await this.$store.dispatch('tasks/getConversation', {
          metadataId,
          taskId,
          context: this,
        });
        if (result?.success) {
          this.conversationList = result.data;
        }
      } catch (err) {
        this.$store.commit('sbError', err.message || err || 'Unknown error!');
      } finally {
        this.commentsLoading = false;
      }
    },

    async refreshData() {
      const checklistId = this.task.project;
      const taskId = this.task._id;
      if (checklistId && taskId) {
        await this.$store.dispatch('tasks/fetchTask', { checklistId, taskId });
        const updatedTask = this.$store.getters['tasks/getTaskById'](taskId);
        if (updatedTask) {
          this.localTask = { ...updatedTask };
        }
      }
      this.$emit('refresh');
    },

    async handleApprove(infoRequest) {
      if (!infoRequest?._id) return;
      const result = await this.$store.dispatch('informationManager/changeRequestStage', {
        requestId: infoRequest._id,
        stage: 2,
      });
      if (result === 'success') {
        await this.refreshData();
      }
    },

    async handleReject(infoRequest) {
      if (!infoRequest?._id) return;
      const result = await this.$store.dispatch('informationManager/changeRequestStage', {
        requestId: infoRequest._id,
        stage: 0,
      });
      if (result === 'success') {
        await this.refreshData();
      }
    },
  },
};
</script>

<style scoped lang="scss">
.task-details-tabbed {
  border-radius: var(--radius-sm);
  background: var(--grey-white);
}

.task-details-tabbed--integrated {
  background: transparent;
  border-radius: 0;
}

.task-details-tabbed__tabs-wrap {
  padding: var(--space-2) 0 0;
}

.task-details-tabbed--integrated .task-details-tabbed__tabs-wrap {
  padding: 0;
}

.task-details-tabbed__panel {
  padding: var(--space-sm) 0 0;
}

.task-details-tabbed--integrated .task-details-tabbed__panel {
  padding: var(--space-sm) 0 0;
}
</style>
